import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { BarChart3, TrendingUp, Download, Users, Trophy, CalendarCheck } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell } from 'recharts';
import { ChartViewport } from '../components/ChartViewport';

const COLORS = ['#dc2626', '#059669', '#2563eb', '#d97706', '#7c3aed', '#db2777', '#0891b2', '#65a30d', '#ea580c', '#6366f1', '#14b8a6', '#f59e0b', '#ef4444', '#22c55e', '#3b82f6'];

export default function AnalyticsPage() {
  const { user } = useAuth();
  const [branchData, setBranchData] = useState([]);
  const [employeeData, setEmployeeData] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [filterBranch, setFilterBranch] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { month };
        const empParams = { month };
        if (filterBranch && filterBranch !== 'all') empParams.branch_id = filterBranch;
        const requests = [api.get('/branches')];
        if (['super_admin', 'management'].includes(user?.system_role)) {
          requests.push(api.get('/analytics/branches', { params }));
        }
        requests.push(api.get('/analytics/employees', { params: empParams }));
        const results = await Promise.all(requests);
        setBranches(results[0].data);
        if (['super_admin', 'management'].includes(user?.system_role)) {
          setBranchData(results[1].data);
          setEmployeeData(results[2].data);
        } else {
          setEmployeeData(results[1].data);
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [month, filterBranch, user]);

  const getBranchName = (bid) => branches.find(b => b.id === bid)?.name?.replace('Konattu ', '') || '-';

  const chartBranchAtt = branchData.map(b => ({
    name: b.branch_name.replace('Konattu ', ''),
    rate: b.attendance_rate,
    staff: b.staff_count
  }));

  const chartBranchAch = branchData.map(b => ({
    name: b.branch_name.replace('Konattu ', ''),
    pct: b.achievements_total > 0 ? Math.round((b.achievements_approved / b.achievements_total) * 100) : 0,
    approved: b.achievements_approved,
    total: b.achievements_total
  }));

  const jobRoleDist = {};
  employeeData.forEach(e => {
    jobRoleDist[e.job_role] = (jobRoleDist[e.job_role] || 0) + 1;
  });
  const pieData = Object.entries(jobRoleDist).map(([name, value]) => ({ name, value }));

  const topPerformers = [...employeeData]
    .sort((a, b) => b.attendance_rate - a.attendance_rate)
    .slice(0, 10);

  const handleExport = (type) => {
    const token = localStorage.getItem('hrms_token');
    const base = process.env.REACT_APP_BACKEND_URL;
    let url = '';
    if (type === 'employees') url = `${base}/api/export/employees`;
    else if (type === 'absences') url = `${base}/api/export/absences?month=${month}`;
    else if (type === 'achievements') url = `${base}/api/export/achievements?month=${month}`;
    const a = document.createElement('a');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => { a.href = URL.createObjectURL(blob); a.download = `${type}.csv`; a.click(); });
  };

  if (loading) return (
    <div className="space-y-6">
      <div className="h-8 w-48 bg-neutral-200 rounded animate-pulse" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-64 bg-neutral-200 rounded animate-pulse" />)}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Analytics</h1>
          <p className="text-neutral-500 text-sm mt-1">Performance insights across branches and employees</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            className="h-10 px-3 border border-neutral-200 rounded-sm text-sm bg-white" data-testid="analytics-month-filter" />
          {user?.system_role !== 'branch_manager' && (
            <Select value={filterBranch} onValueChange={setFilterBranch}>
              <SelectTrigger className="w-44 rounded-sm" data-testid="analytics-branch-filter">
                <SelectValue placeholder="All Branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" className="rounded-sm" onClick={() => handleExport('employees')} data-testid="export-employees-btn">
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
        </div>
      </div>

      <Tabs defaultValue={['super_admin', 'management'].includes(user?.system_role) ? 'branches' : 'employees'} className="w-full">
        <TabsList className="rounded-sm">
          {['super_admin', 'management'].includes(user?.system_role) && (
            <TabsTrigger value="branches" className="rounded-sm" data-testid="tab-branches">Branch Performance</TabsTrigger>
          )}
          <TabsTrigger value="employees" className="rounded-sm" data-testid="tab-employees">Employee Performance</TabsTrigger>
          <TabsTrigger value="overview" className="rounded-sm" data-testid="tab-overview">Overview</TabsTrigger>
        </TabsList>

        {/* Branch Performance Tab */}
        {['super_admin', 'management'].includes(user?.system_role) && (
          <TabsContent value="branches" className="mt-4 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Attendance Rate Chart */}
              <Card className="border border-neutral-200 shadow-sm rounded-md">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-neutral-700 flex items-center gap-2">
                    <CalendarCheck className="h-4 w-4 text-red-600" /> Attendance Rate by Branch
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="h-80">
                    <ChartViewport testId="branch-attendance-chart">
                      <BarChart data={chartBranchAtt} layout="vertical" margin={{ left: 10, right: 20, top: 10, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(val) => `${val}%`} contentStyle={{ fontSize: 12, borderRadius: 4 }} />
                        <Bar dataKey="rate" fill="#dc2626" radius={[0, 4, 4, 0]} barSize={14} />
                      </BarChart>
                    </ChartViewport>
                  </div>
                </CardContent>
              </Card>

              {/* Achievement Chart */}
              <Card className="border border-neutral-200 shadow-sm rounded-md">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-neutral-700 flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-amber-500" /> Achievements by Branch
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="h-80">
                    <ChartViewport testId="branch-achievement-chart">
                      <BarChart data={chartBranchAch} layout="vertical" margin={{ left: 10, right: 20, top: 10, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                        <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 10 }} />
                        <Tooltip
                          formatter={(val, _n, item) => [`${val}% (${item.payload.approved}/${item.payload.total})`, 'Approval Rate']}
                          contentStyle={{ fontSize: 12, borderRadius: 4 }}
                        />
                        <Bar dataKey="pct" fill="#059669" radius={[0, 4, 4, 0]} barSize={14} name="Approval %" />
                      </BarChart>
                    </ChartViewport>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Branch Table */}
            <Card className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-neutral-700">Branch Summary</CardTitle>
              </CardHeader>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-neutral-50">
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Branch</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Staff</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Att. Rate</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Achievements</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {branchData.map(b => (
                      <TableRow key={b.branch_id} className="hover:bg-neutral-50">
                        <TableCell className="text-sm font-medium text-neutral-900">{b.branch_name}</TableCell>
                        <TableCell className="text-sm text-neutral-600">{b.staff_count}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-2 bg-neutral-200 rounded-full overflow-hidden">
                              <div className="h-full bg-red-600 rounded-full" style={{ width: `${Math.min(b.attendance_rate, 100)}%` }} />
                            </div>
                            <span className="text-xs font-semibold text-neutral-700">{b.attendance_rate}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-neutral-600">{b.achievements_approved}/{b.achievements_total}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>
        )}

        {/* Employee Performance Tab */}
        <TabsContent value="employees" className="mt-4 space-y-6">
          <Card className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-neutral-700">Employee Performance Rankings</CardTitle>
                <Badge variant="secondary" className="text-xs">{employeeData.length} employees</Badge>
              </div>
            </CardHeader>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-neutral-50">
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 w-10">#</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Name</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden sm:table-cell">Role</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden md:table-cell">Branch</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Attendance</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden sm:table-cell">Achievements</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden md:table-cell">Tasks</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden lg:table-cell">Test Avg</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topPerformers.map((e, i) => (
                    <TableRow key={e.employee_id} className="hover:bg-neutral-50" data-testid={`emp-perf-row-${e.employee_id}`}>
                      <TableCell>
                        {i < 3 ? (
                          <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold
                            ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-neutral-200 text-neutral-700' : 'bg-orange-100 text-orange-700'}`}>
                            {i + 1}
                          </span>
                        ) : <span className="text-xs text-neutral-500">{i + 1}</span>}
                      </TableCell>
                      <TableCell className="text-sm font-medium text-neutral-900">{e.name}</TableCell>
                      <TableCell className="text-sm text-neutral-600 hidden sm:table-cell">
                        <Badge variant="secondary" className={`text-xs ${['Sales Girl', 'Sales Man'].includes(e.job_role) ? 'bg-red-50 text-red-700' : ''}`}>
                          {e.job_role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-neutral-600 hidden md:table-cell">{getBranchName(e.branch_id)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-12 h-2 bg-neutral-200 rounded-full overflow-hidden">
                            <div className="h-full bg-red-600 rounded-full" style={{ width: `${Math.min(e.attendance_rate, 100)}%` }} />
                          </div>
                          <span className="text-xs font-semibold text-neutral-700">{e.attendance_rate}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-neutral-600 hidden sm:table-cell">{e.achievements_approved}</TableCell>
                      <TableCell className="text-sm text-neutral-600 hidden md:table-cell">{e.tasks_completed}/{e.tasks_total}</TableCell>
                      <TableCell className="text-sm text-neutral-600 hidden lg:table-cell">{e.avg_test_score > 0 ? `${e.avg_test_score}%` : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Workforce Distribution */}
            <Card className="border border-neutral-200 shadow-sm rounded-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-neutral-700 flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-600" /> Workforce Distribution
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="h-64">
                  <ChartViewport testId="role-distribution-chart">
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={44} outerRadius={80} dataKey="value" label={false}>
                        {pieData.map((entry, i) => <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 4 }} />
                    </PieChart>
                  </ChartViewport>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mt-3" data-testid="role-distribution-legend">
                  {pieData.map((entry, index) => <li key={entry.name} className="flex items-start gap-2 text-sm min-w-0" data-testid={`role-distribution-${index}`}>
                    <span className="w-3 h-3 shrink-0 rounded-sm mt-1" style={{ backgroundColor: COLORS[index % COLORS.length] }} aria-hidden="true" />
                    <span className="flex-1 break-words">{entry.name}</span><strong>{entry.value}</strong>
                  </li>)}
                </ul>
              </CardContent>
            </Card>

            {/* Quick Export */}
            <Card className="border border-neutral-200 shadow-sm rounded-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-neutral-700 flex items-center gap-2">
                  <Download className="h-4 w-4 text-green-600" /> Export Reports
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                <p className="text-xs text-neutral-500">Download CSV reports for the selected period</p>
                <Button variant="outline" className="w-full justify-start rounded-sm" onClick={() => handleExport('employees')}
                  data-testid="export-employees-csv">
                  <Users className="h-4 w-4 mr-2 text-blue-600" /> Employee Directory
                </Button>
                <Button variant="outline" className="w-full justify-start rounded-sm" onClick={() => handleExport('absences')}
                  data-testid="export-absences-csv">
                  <CalendarCheck className="h-4 w-4 mr-2 text-red-600" /> Absence Report ({month})
                </Button>
                <Button variant="outline" className="w-full justify-start rounded-sm" onClick={() => handleExport('achievements')}
                  data-testid="export-achievements-csv">
                  <Trophy className="h-4 w-4 mr-2 text-amber-600" /> Achievements Report ({month})
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
